import type { FocusEventHandler, MouseEventHandler } from 'react';
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
    isCageDashboardCacheWarm,
    locationTableCageDashboardTarget,
    prefetchCageDashboard,
} from '@/domain/cages/prefetchCageDashboard';

const HOVER_PREFETCH_DELAY_MS = 100;

interface UseLocationCagePrefetchInput {
    cageId: number;
    fishGroupId?: number;
    /** Smolt production tank. Its dashboard overview is read at the `all` daypart. */
    smolt: boolean;
    enabled: boolean;
}

/**
 * Prefetch a location-table pen once the pointer or keyboard has rested on it.
 * A sweep shorter than the delay does not ask. A pen whose dashboard cache is
 * still fresh is not asked again; once that cache is stale, a later rest asks again.
 */
export function useLocationCagePrefetch(input: UseLocationCagePrefetchInput): {
    onMouseEnter: MouseEventHandler<HTMLAnchorElement>;
    onMouseLeave: MouseEventHandler<HTMLAnchorElement>;
    onFocus: FocusEventHandler<HTMLAnchorElement>;
    onBlur: FocusEventHandler<HTMLAnchorElement>;
} {
    const { cageId, fishGroupId, smolt, enabled } = input;
    const queryClient = useQueryClient();
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inside = useRef({ pointer: false, focus: false });
    const asked = useRef(new Set<string>());

    const clearTimer = () => {
        if (timer.current != null) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    };

    const fire = () => {
        timer.current = null;
        if (!enabled) {
            return;
        }
        const target = locationTableCageDashboardTarget({ cageId, fishGroupId, smolt });
        if (isCageDashboardCacheWarm(queryClient, target)) {
            return;
        }
        const id = `${target.cageId}:${target.fishGroupId ?? ''}:${target.daypartTag}`;
        if (asked.current.has(id)) {
            return;
        }
        asked.current.add(id);
        void prefetchCageDashboard(queryClient, target).finally(() => {
            asked.current.delete(id);
        });
    };

    const sync = () => {
        if (!inside.current.pointer && !inside.current.focus) {
            clearTimer();
            return;
        }
        if (timer.current != null) {
            return;
        }
        timer.current = setTimeout(fire, HOVER_PREFETCH_DELAY_MS);
    };

    useEffect(
        () => () => {
            if (timer.current != null) {
                clearTimeout(timer.current);
                timer.current = null;
            }
        },
        []
    );

    return {
        onMouseEnter: () => {
            inside.current.pointer = true;
            sync();
        },
        onMouseLeave: () => {
            inside.current.pointer = false;
            sync();
        },
        onFocus: () => {
            inside.current.focus = true;
            sync();
        },
        onBlur: () => {
            inside.current.focus = false;
            sync();
        },
    };
}
